import { z } from 'zod';

import { entityIdSchema, isoDateSchema, pageMetaSchema } from './common.js';
import { priceLineSourceReferenceSchema } from './pricing.js';

export const promoBenefitTargetTypeSchema = z.enum([
  'stay_rate',
  'option',
  'legacy_labeled_benefit',
]);

/**
 * Snapshot métier conservé sur le Code promo. `legacy_labeled_benefit` est une
 * provenance de migration : les routes de création la refusent explicitement.
 */
export const promoBenefitSchema = z
  .object({
    label: z.string().trim().min(1).max(240),
    targetType: promoBenefitTargetTypeSchema,
    targetId: priceLineSourceReferenceSchema.nullable(),
    quantity: z.number().int().positive().max(10_000).nullable(),
    estimatedUnitCents: z.number().int().positive().max(1_000_000_000),
  })
  .strict()
  .superRefine((benefit, context) => {
    const migrated = benefit.targetType === 'legacy_labeled_benefit';
    if (migrated !== (benefit.targetId === null)) {
      context.addIssue({
        code: 'custom',
        path: ['targetId'],
        message: migrated
          ? 'Une prestation legacy est résolue par son libellé historique.'
          : 'La cible canonique de la prestation est obligatoire.',
      });
    }
  });

export const promoBenefitMetadataSchema = z
  .object({
    benefit: promoBenefitSchema.nullable().optional(),
  })
  .loose();

export const promoBenefitCatalogQuerySchema = z.object({
  cursor: z.string().trim().min(1).max(1_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).optional(),
  targetType: z.enum(['stay_rate', 'option']).optional(),
});

export const promoBenefitCatalogItemSchema = z
  .object({
    id: entityIdSchema,
    label: z.string().trim().min(1).max(240),
    targetType: z.enum(['stay_rate', 'option']),
    unitPriceCents: z.number().int().nonnegative(),
    active: z.boolean(),
    available: z.boolean(),
    sourceKey: z.enum([
      'cg_config_v109',
      'cg_reservation_config_v92',
      'cg_reservation_module_config_v89',
    ]),
    sourceRevision: z.string().trim().min(1).max(240),
    validFrom: isoDateSchema.nullable(),
    validUntil: isoDateSchema.nullable(),
  })
  .strict();

export const promoBenefitCatalogPageSchema = z
  .object({
    items: z.array(promoBenefitCatalogItemSchema),
    page: pageMetaSchema,
  })
  .strict();

export type PromoBenefit = z.infer<typeof promoBenefitSchema>;
export type PromoBenefitMetadata = z.infer<typeof promoBenefitMetadataSchema>;
export type PromoBenefitCatalogQuery = z.output<typeof promoBenefitCatalogQuerySchema>;
export type PromoBenefitCatalogItem = z.infer<typeof promoBenefitCatalogItemSchema>;
export type PromoBenefitCatalogPage = z.infer<typeof promoBenefitCatalogPageSchema>;

function metadataObject(
  metadata: string | Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  let parsed: unknown = metadata;
  if (typeof metadata === 'string') {
    try {
      parsed = JSON.parse(metadata) as unknown;
    } catch {
      throw new Error('PROMO_BENEFIT_METADATA_INVALID');
    }
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('PROMO_BENEFIT_METADATA_INVALID');
  }
  return { ...(parsed as Readonly<Record<string, unknown>>) };
}

export function promoBenefitFromMetadata(
  metadata: string | Readonly<Record<string, unknown>>,
): PromoBenefit | null {
  const parsed = promoBenefitMetadataSchema.safeParse(metadataObject(metadata));
  if (!parsed.success) throw new Error('PROMO_BENEFIT_METADATA_INVALID');
  return parsed.data.benefit ?? null;
}

export function promoBenefitMetadataJson(
  metadata: string | Readonly<Record<string, unknown>>,
  benefit: PromoBenefit | null,
): string {
  const parsed = metadataObject(metadata);
  if (benefit === null) {
    delete parsed.benefit;
  } else {
    parsed.benefit = promoBenefitSchema.parse(benefit);
  }
  return JSON.stringify(parsed);
}
